import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { IconArrowLeft } from '@tabler/icons-react';
import axios from 'axios';
import * as Sentry from '@sentry/react';
import PropTypes from 'prop-types';

import appSettings from '../app-settings';
import { templateTypes, WELCOME_SERIES } from '../core/constants';
import IconButton from '../components/buttons/IconButton';
import Loader from '../components/loader/Loader';
import AlertMessage from '../components/alerts/AlertMessage';
import WelcomeEmailAccordion from '../components/onboarding/WelcomeEmailAccordion';
import { LaunchWelcomeSeriesButton, LaunchSecondWelcomeSeriesButton } from '../components/welcome-series';
import { useDomains } from '../store/domains/hooks';

const EMAIL_TEMPLATES_URL = `${appSettings.baseUrl}/email-templates`;

// First welcome series: 4 emails at recurring template order 0-3.
const firstSeriesInitial = [
  { title: 'Welcome Email #1', image: 'blank.svg', source: 'blank.json', type: templateTypes.recurring, order: 0 },
  { title: 'Welcome Email #2', image: 'blank.svg', source: 'blank.json', type: templateTypes.recurring, order: 1 },
  { title: 'Welcome Email #3', image: 'blank.svg', source: 'blank.json', type: templateTypes.recurring, order: 2 },
  { title: 'Welcome Email #4', image: 'letter.svg', source: 'welcome-4.json', type: templateTypes.recurring, order: 3 },
];

// Second welcome series: 5 emails at recurring template order 4-8.
const secondSeriesInitial = Array.from({ length: WELCOME_SERIES.second.count }, (_, index) => ({
  title: `Second Welcome Email #${index + 1}`,
  image: 'blank.svg',
  source: 'blank.json',
  type: templateTypes.recurring,
  order: WELCOME_SERIES.second.startOrder + index,
}));

/** Merge saved recurring templates into the matching slot (by order). */
const mergeSavedTemplates = (slots, savedTemplates) =>
  slots.map((slot) => {
    const saved = savedTemplates.find((t) => t.order === slot.order);
    if (!saved) return { ...slot };
    return {
      ...slot,
      design: saved.design,
      downloadUrl: saved.downloadUrl,
      templateId: saved.templateId,
      templateVersion: saved.templateVersion,
      emailConfig: saved.emailConfig,
      type: saved.type ?? slot.type,
    };
  });

const WelcomeSeriesEditSection = ({
  title,
  description,
  templates,
  keyPrefix,
  activeKey,
  onToggle,
  onSaveSuccess,
  launchButton,
}) => (
  <div className="space-y-4">
    <div className="flex items-start justify-between gap-3">
      <div className="space-y-1">
        <h2 className="text-h4">{title}</h2>
        {description && <p className="text-body2 text-gray-500">{description}</p>}
      </div>

      {launchButton}
    </div>

    <div className="bg-white divide-y divide-gray-50 rounded-lg border border-gray-100 bg-white-100">
      {templates.map((template, index) => {
        const itemKey = `${keyPrefix}-${index}`;
        return (
          <WelcomeEmailAccordion
            key={itemKey}
            template={template}
            isOpen={activeKey === itemKey}
            onToggle={() => onToggle(itemKey)}
            onSaveSuccess={onSaveSuccess}
          />
        );
      })}
    </div>
  </div>
);

WelcomeSeriesEditSection.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  templates: PropTypes.array.isRequired,
  keyPrefix: PropTypes.string.isRequired,
  activeKey: PropTypes.string,
  onToggle: PropTypes.func.isRequired,
  onSaveSuccess: PropTypes.func.isRequired,
  launchButton: PropTypes.node,
};

const WelcomeSeriesEdit = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [firstSeries, setFirstSeries] = useState(firstSeriesInitial);
  const [secondSeries, setSecondSeries] = useState(secondSeriesInitial);
  // A single active accordion key (e.g. "first-0" / "second-2") so only one
  // heavy email editor is open at a time across both sections.
  const [activeKey, setActiveKey] = useState(null);

  const { getDomains } = useDomains();

  const fetchTemplates = useCallback(async () => {
    setLoading(true);
    try {
      const { data: savedTemplates } = await axios.get(EMAIL_TEMPLATES_URL, {
        params: { type: templateTypes.recurring },
      });
      setFirstSeries(mergeSavedTemplates(firstSeriesInitial, savedTemplates));
      setSecondSeries(mergeSavedTemplates(secondSeriesInitial, savedTemplates));
    } catch (err) {
      Sentry.captureException(err);
      setError(err?.message || 'Failed to load welcome series templates');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    fetchTemplates();
    getDomains();
  }, [fetchTemplates, getDomains]);

  const handleToggle = (key) => {
    setActiveKey((prev) => (prev === key ? null : key));
  };

  const handleSaveSuccess = () => {
    fetchTemplates();
    setActiveKey(null);
  };

  return (
    <div className="w-full space-y-8">
      <div className="flex items-center space-x-1">
        <IconButton
          Icon={<IconArrowLeft size={20} />}
          color="transparent"
          className="-ml-2"
          onClick={() => navigate('/welcome-series')}
          srOnly="Go back"
        />

        <h1 className="text-h3">Edit Welcome Series</h1>
      </div>

      {loading ? (
        <div className="relative flex h-96 items-center justify-center">
          <Loader />
        </div>
      ) : (
        <>
          <WelcomeSeriesEditSection
            title="Welcome Series"
            description=""
            templates={firstSeries}
            keyPrefix="first"
            activeKey={activeKey}
            onToggle={handleToggle}
            onSaveSuccess={handleSaveSuccess}
            launchButton={<LaunchWelcomeSeriesButton onError={setError} />}
          />

          <WelcomeSeriesEditSection
            title="Second Welcome Series"
            description="Sent about a year later to contacts who didn't engage with the first Welcome Series."
            templates={secondSeries}
            keyPrefix="second"
            activeKey={activeKey}
            onToggle={handleToggle}
            onSaveSuccess={handleSaveSuccess}
            launchButton={<LaunchSecondWelcomeSeriesButton onError={setError} />}
          />
        </>
      )}

      <AlertMessage
        open={!!error}
        message={typeof error === 'string' ? error : 'Oops, something went wrong!'}
        onClose={() => setError(false)}
        severity="error"
      />
    </div>
  );
};

export default WelcomeSeriesEdit;
